import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiDownload } from '../../lib/api';
import { Download, AlertTriangle, Loader2, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

function todayISO() {
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 + now.getTimezoneOffset()) * 60_000);
  return ist.toISOString().slice(0, 10);
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function SingleDatePicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const dateObj = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testid}
          className={cn('justify-start text-left font-normal text-sm h-[38px] border-slate-200', !value && 'text-muted-foreground')}>
          <CalendarIcon className="mr-2 h-3.5 w-3.5" />
          {dateObj ? format(dateObj, 'dd MMM yyyy') : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" initialFocus selected={dateObj} defaultMonth={dateObj}
          onSelect={(d) => { if (d) { onChange(d.toISOString().slice(0, 10)); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}

function MonthYearPicker({ value, onChange, testid }) {
  const [y, m] = value.split('-').map(Number);
  const years = Array.from({ length: 6 }, (_, i) => y - 3 + i);
  return (
    <div className="flex gap-1.5" data-testid={testid}>
      <select data-testid={`${testid}-month`} value={m} onChange={(e) => onChange(`${y}-${String(e.target.value).padStart(2, '0')}`)}
        className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white h-[38px]">
        {MONTH_NAMES.map((mn, i) => <option key={mn} value={i + 1}>{mn}</option>)}
      </select>
      <select data-testid={`${testid}-year`} value={y} onChange={(e) => onChange(`${e.target.value}-${String(m).padStart(2, '0')}`)}
        className="border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white h-[38px]">
        {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}

const KV = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-800 mt-0.5">{value}</div>
  </div>
);

export default function ProgressReport() {
  const [periodType, setPeriodType] = useState('week'); // 'week' | 'month'
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [weekStart, setWeekStart] = useState(todayISO());
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    apiGet('/projects').then((p) => {
      const list = p?.items || p || [];
      setProjects(list);
      if (list.length) setProjectId(list[0].id);
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const path = periodType === 'week' ? '/reports/wpr' : '/reports/mpr';
      const params = periodType === 'week' ? { project_id: projectId, week_start: weekStart } : { project_id: projectId, month };
      const d = await apiGet(path, params);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load report');
      setData(null);
    }
    setLoading(false);
  }, [periodType, projectId, weekStart, month]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const path = periodType === 'week'
        ? `/reports/wpr.pdf?project_id=${encodeURIComponent(projectId)}&week_start=${weekStart}`
        : `/reports/mpr.pdf?project_id=${encodeURIComponent(projectId)}&month=${month}`;
      const fname = periodType === 'week' ? `WPR_${weekStart}.pdf` : `MPR_${month}.pdf`;
      await apiDownload(path, fname);
    } catch {
      toast.error('PDF generation failed');
    }
    setDownloading(false);
  };

  return (
    <div data-testid="progress-report-page">
      <PageHeader
        overline="Project Progress Reports"
        title="Weekly / Monthly Progress Report"
        subtitle="Roll-ups computed from finalised DPRs — quantities, safety, manpower, materials, and hindrances."
      />

      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-end gap-3 flex-wrap mb-5">
        <div>
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Project</label>
          <select data-testid="progress-report-project" value={projectId} onChange={e => setProjectId(e.target.value)}
            className="border border-slate-200 rounded-sm text-sm px-3 py-2 min-w-[220px]">
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Period</label>
          <div className="flex border border-slate-200 rounded-sm overflow-hidden">
            <button data-testid="progress-report-period-week" onClick={() => setPeriodType('week')}
              className={`px-3 py-2 text-sm font-semibold ${periodType === 'week' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-600'}`}>Weekly</button>
            <button data-testid="progress-report-period-month" onClick={() => setPeriodType('month')}
              className={`px-3 py-2 text-sm font-semibold ${periodType === 'month' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-600'}`}>Monthly</button>
          </div>
        </div>
        {periodType === 'week' ? (
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Any day in week</label>
            <SingleDatePicker value={weekStart} onChange={setWeekStart} testid="progress-report-week-start" />
          </div>
        ) : (
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Month</label>
            <MonthYearPicker value={month} onChange={setMonth} testid="progress-report-month" />
          </div>
        )}
        <button data-testid="progress-report-download-pdf" onClick={downloadPdf} disabled={downloading || !data}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-sm bg-[#3b4872] text-white font-semibold hover:bg-[#2c3658] disabled:opacity-50 ml-auto">
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download PDF
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>}

      {!loading && data && (
        <div className="space-y-5" data-testid="progress-report-content">
          <div className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <KV label="Project" value={data.project_name} />
            <KV label="Period" value={data.period_label} />
            <KV label="DPRs Finalised" value={`${data.dpr_coverage.finalised_days} of ${data.dpr_coverage.total_days} days`} />
            <KV label="Manpower (avg/day)" value={data.narrative.manpower.avg_present ?? '—'} />
          </div>

          {data.dpr_coverage.missing_dates.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-2 text-amber-900" data-testid="progress-report-gap-warning">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div className="text-sm">
                <b>{data.dpr_coverage.missing_dates.length} day(s)</b> with no finalised DPR: {data.dpr_coverage.missing_dates.join(', ')}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 font-display font-bold text-sm">Quantity Progress (WBS)</div>
            {!data.has_wbs ? (
              <div className="p-4 text-sm text-slate-500" data-testid="progress-report-no-wbs">
                No quantity data — WBS is not set up for this project. Narrative roll-up below is still available.
              </div>
            ) : data.wbs_table.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No WBS packages found for this project.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Package</th>
                    <th className="text-right px-4 py-2">Planned</th><th className="text-right px-4 py-2">This Period</th>
                    <th className="text-right px-4 py-2">Cumulative</th><th className="text-right px-4 py-2">% Complete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.wbs_table.map(r => (
                    <tr key={r.wbs_id}>
                      <td className="px-4 py-2">{r.wbs_code || '—'}</td>
                      <td className="px-4 py-2">{r.name}</td>
                      <td className="px-4 py-2 text-right">{r.planned_qty || '—'} {r.unit}</td>
                      <td className="px-4 py-2 text-right">{r.period_actual} {r.unit}</td>
                      <td className="px-4 py-2 text-right">{r.cumulative_actual} {r.unit}</td>
                      <td className="px-4 py-2 text-right font-semibold">{r.pct_complete != null ? `${r.pct_complete}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="font-display font-bold text-sm mb-3">Safety &amp; Quality</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <KV label="Toolbox Talks" value={data.narrative.safety.toolbox_talks} />
                <KV label="Incidents" value={data.narrative.safety.incidents} />
                <KV label="PPE Violations" value={data.narrative.safety.ppe_violations} />
                <KV label="NCRs Raised" value={data.narrative.safety.ncrs_raised} />
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="font-display font-bold text-sm mb-3">Hindrances</div>
              {data.narrative.hindrances.length === 0 ? (
                <div className="text-sm text-slate-400">No hindrances logged this period.</div>
              ) : (
                <ul className="text-sm space-y-1.5">
                  {data.narrative.hindrances.map((h, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span>{h.title}</span>
                      <span className="text-slate-500">{h.responsibility} · {h.impact_days}d</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="font-display font-bold text-sm mb-3">Materials Received (Period)</div>
              {data.narrative.materials_received.length === 0 ? (
                <div className="text-sm text-slate-400">No materials received this period.</div>
              ) : (
                <ul className="text-sm space-y-1.5">
                  {data.narrative.materials_received.map((m, i) => (
                    <li key={i} className="flex justify-between gap-2"><span>{m.description}</span><span className="text-slate-500">{m.qty} {m.unit}</span></li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="font-display font-bold text-sm mb-3">Materials Issued (Period)</div>
              {data.narrative.materials_issued.length === 0 ? (
                <div className="text-sm text-slate-400">No materials issued this period.</div>
              ) : (
                <ul className="text-sm space-y-1.5">
                  {data.narrative.materials_issued.map((m, i) => (
                    <li key={i} className="flex justify-between gap-2"><span>{m.description}</span><span className="text-slate-500">{m.qty} {m.unit}</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
